/**
 * Replaces placeholders in a string with provided values.
 * Placeholders should be in the format {key} or {{key}} or :key or $key.
 * 
 * @param template - The template string containing placeholders
 * @param replacements - Object with key-value pairs for replacement
 * @param options - Configuration options for placeholder format
 * @returns The string with all placeholders replaced
 * 
 * @example
 * ```ts
 * replacePlaceholders('assets/images/{name}_{locale}.png', { name: 'screenshot', locale: 'en' })
 * // returns 'assets/images/screenshot_en.png'
 * 
 * replacePlaceholders('Hello {name}, you have {count} messages', { name: 'John', count: 5 })
 * // returns 'Hello John, you have 5 messages'
 * ```
 */
export function replacePlaceholders(
  template: string,
  replacements: Record<string, string | number>,
  options: { format?: 'curly' | 'double-curly' | 'colon' | 'dollar' } = {}
): string {
  const format = options.format || 'curly';
  
  let result = template;
  
  for (const [key, value] of Object.entries(replacements)) {
    let pattern: RegExp;
    
    switch (format) {
      case 'double-curly':
        pattern = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
        break;
      case 'colon':
        pattern = new RegExp(`:${key}\\b`, 'g');
        break;
      case 'dollar':
        pattern = new RegExp(`\\$${key}\\b`, 'g');
        break;
      case 'curly':
      default:
        pattern = new RegExp(`\\{${key}\\}`, 'g');
        break;
    }
    
    result = result.replace(pattern, String(value));
  }
  
  return result;
}